import { lazy, Suspense, useMemo, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Building2, Calendar, LayoutGrid, Loader2 } from 'lucide-react';
import type { PortalCycleSummary } from '@shared/portal-api-types';
import { fetchPortalContext, fetchPortalCycles } from '@/lib/portalApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { PortalShell } from './PortalShell';
import {
  PortalErrorBlock,
  PortalLoadingBlock,
  portalLabelClass,
  portalPageSubtitleClass,
  portalPageTitleClass,
} from './portalUi';
import { buildPortalHref } from './portalCycleQuery';

const CyclesCalendar = lazy(() => import('@/components/CyclesCalendar'));

type CycleViewMode = 'cards' | 'calendar';

function queryErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function cycleYear(cycle: PortalCycleSummary): number | null {
  const raw = cycle.startDate ?? cycle.endDate;
  if (!raw) return null;
  const year = Number(raw.slice(0, 4));
  return Number.isFinite(year) ? year : null;
}

function dateRangeLabel(cycle: PortalCycleSummary): string {
  const start = cycle.startDate ? new Date(cycle.startDate) : null;
  const end = cycle.endDate ? new Date(cycle.endDate) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'Dates unavailable';
  }
  const fmt = new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

function cycleStatusLabel(cycle: PortalCycleSummary): 'Upcoming' | 'Active' | 'Completed' | 'Undated' {
  if (!cycle.startDate || !cycle.endDate) return 'Undated';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(cycle.startDate);
  const end = new Date(cycle.endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (end < today) return 'Completed';
  if (start > today) return 'Upcoming';
  return 'Active';
}

function statusBadgeClass(status: ReturnType<typeof cycleStatusLabel>): string {
  switch (status) {
    case 'Active':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'Upcoming':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'Completed':
      return 'border-border bg-muted text-muted-foreground';
    case 'Undated':
      return 'border-border bg-muted text-muted-foreground';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function PortalCyclesPage() {
  const urlSearch = useSearch();
  const [, setLocation] = useLocation();
  const [viewMode, setViewMode] = useState<CycleViewMode>('cards');
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | 'all'>('all');

  const { data: context, isLoading: contextLoading, isError: contextError, error: contextErrorDetail } = useQuery({
    queryKey: ['portal', 'context'],
    queryFn: fetchPortalContext,
  });

  const {
    data: cyclesData,
    isLoading: cyclesLoading,
    isError: cyclesError,
    error: cyclesErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'cycles'],
    queryFn: fetchPortalCycles,
    enabled: !!context?.features.cycles,
  });

  const retailerNameById = useMemo(
    () => new Map((context?.allowedRetailers ?? []).map((retailer) => [retailer.id, retailer.name])),
    [context?.allowedRetailers],
  );

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    for (const cycle of cyclesData?.items ?? []) {
      const year = cycleYear(cycle);
      if (year != null) years.add(year);
    }
    return Array.from(years).sort((left, right) => right - left);
  }, [cyclesData?.items]);

  const filteredCycles = useMemo(() => {
    return (cyclesData?.items ?? []).filter((cycle) => {
      if (selectedRetailerId !== 'all' && cycle.retailerId !== selectedRetailerId) return false;
      if (selectedYear === 'all') return true;
      const year = cycleYear(cycle);
      return year === selectedYear;
    });
  }, [cyclesData?.items, selectedRetailerId, selectedYear]);

  const calendarCycles = useMemo(
    () => filteredCycles.map((cycle) => ({
      id: cycle.id,
      code: cycle.name,
      name: cycle.name,
      retailerId: cycle.retailerId,
      retailer_name: retailerNameById.get(cycle.retailerId),
      startDate: cycle.startDate ?? undefined,
      endDate: cycle.endDate ?? undefined,
      locked_flag: false,
    })),
    [filteredCycles, retailerNameById],
  );

  const calendarRetailers = useMemo(
    () => (context?.allowedRetailers ?? []).map((retailer) => ({
      id: retailer.id,
      name: retailer.name,
    })),
    [context?.allowedRetailers],
  );

  const openCampaignsForCycle = (cycleId: number) => {
    setLocation(buildPortalHref('/portal', urlSearch, { cycleId }), { replace: false });
  };

  if (contextLoading) return <PortalLoadingBlock />;
  if (contextError || !context) {
    return <PortalErrorBlock message={queryErrorMessage(contextErrorDetail, 'Could not load portal context.')} />;
  }

  if (!context.features.cycles) {
    return (
      <PortalShell context={context}>
        <PortalErrorBlock message="Cycles are not enabled for your account." />
      </PortalShell>
    );
  }

  const showRetailerFilter = context.allowedRetailers.length > 1;

  return (
    <PortalShell context={context}>
      <div className="space-y-6">
        <div>
          <h2 className={portalPageTitleClass}>Cycles</h2>
          <p className={portalPageSubtitleClass}>
            Browse booking cycles for {context.allowedRetailers.map((retailer) => retailer.name).join(', ')}.
          </p>
        </div>

        <Card>
          <CardContent className="flex flex-wrap items-end gap-3 pt-6">
            <label className="flex flex-col gap-1 text-sm">
              <span className={portalLabelClass}>Year</span>
              <select
                className="min-w-[120px] rounded border border-border bg-background px-3 py-2"
                value={selectedYear === 'all' ? 'all' : String(selectedYear)}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedYear(value === 'all' ? 'all' : Number(value));
                }}
              >
                <option value="all">All years</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </label>

            {showRetailerFilter ? (
              <label className="flex flex-col gap-1 text-sm">
                <span className={portalLabelClass}>Retailer</span>
                <select
                  className="min-w-[180px] rounded border border-border bg-background px-3 py-2"
                  value={selectedRetailerId === 'all' ? 'all' : String(selectedRetailerId)}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedRetailerId(value === 'all' ? 'all' : Number(value));
                  }}
                >
                  <option value="all">All retailers</option>
                  {context.allowedRetailers.map((retailer) => (
                    <option key={retailer.id} value={retailer.id}>{retailer.name}</option>
                  ))}
                </select>
              </label>
            ) : null}

            <div className="ml-auto flex flex-col gap-1 text-sm">
              <span className={portalLabelClass}>View</span>
              <ToggleGroup
                type="single"
                value={viewMode}
                onValueChange={(value) => {
                  if (value === 'cards' || value === 'calendar') {
                    setViewMode(value);
                  }
                }}
              >
                <ToggleGroupItem value="cards" aria-label="Card view">
                  <LayoutGrid className="mr-2 h-4 w-4" />
                  Cards
                </ToggleGroupItem>
                <ToggleGroupItem value="calendar" aria-label="Calendar view">
                  <Calendar className="mr-2 h-4 w-4" />
                  Calendar
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </CardContent>
        </Card>

        {cyclesLoading ? (
          <PortalLoadingBlock />
        ) : cyclesError ? (
          <PortalErrorBlock message={queryErrorMessage(cyclesErrorDetail, 'Could not load cycles.')} />
        ) : filteredCycles.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground/40" />
              <p className="text-lg font-medium text-muted-foreground">No cycles match</p>
              <p className="max-w-md text-sm text-muted-foreground">
                Try another year or retailer filter.
              </p>
            </CardContent>
          </Card>
        ) : viewMode === 'calendar' ? (
          <Suspense
            fallback={(
              <Card>
                <CardContent className="flex items-center justify-center gap-2 py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <span className="text-muted-foreground">Loading calendar…</span>
                </CardContent>
              </Card>
            )}
          >
            <CyclesCalendar
              cycles={calendarCycles}
              retailers={calendarRetailers}
              readOnly
              onCycleSelect={openCampaignsForCycle}
            />
          </Suspense>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filteredCycles.map((cycle) => {
              const status = cycleStatusLabel(cycle);
              return (
                <Card
                  key={cycle.id}
                  className="border-2 transition-shadow hover:shadow-md"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <CardTitle className="truncate text-base">{cycle.name}</CardTitle>
                        </div>
                        <CardDescription className="mt-1">{dateRangeLabel(cycle)}</CardDescription>
                      </div>
                      <Badge variant="outline" className={cn('shrink-0', statusBadgeClass(status))}>
                        {status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {showRetailerFilter ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="h-4 w-4 shrink-0" />
                        <span>{retailerNameById.get(cycle.retailerId) ?? 'Retailer'}</span>
                      </div>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" onClick={() => openCampaignsForCycle(cycle.id)}>
                        View campaigns
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </PortalShell>
  );
}
